// Question renderer — dispatches to a specialized component per question
// type. Also exports the pure validation + conditional-logic helpers so
// they're usable from the flow parent.
import { useEffect, useRef, useState } from "react";
import { apiGet } from "@/lib/api";
import { C } from "@/lib/brand";

export default function QuestionRenderer({ question, value, onChange }) {
  // Allow the question schema to opt into a feet+inches input via either
  // an explicit `inputAs: "feet_inches"` hint, a question whose unit is
  // "inches" (or just "in"), or — as a friendly fallback — any question
  // whose key matches a height-in-inches naming convention. We accept
  // any questionType here (backend currently ships height as plain text)
  // so members never have to do mental math.
  const looksLikeHeightInches =
    question.inputAs === "feet_inches" ||
    (question.unit && /^(in|inch|inches)$/i.test(question.unit)) ||
    /^height(_in(ches)?)?$/i.test(question.questionKey || "") ||
    /height/i.test(question.questionKey || "");
  if (looksLikeHeightInches) {
    return <FeetInchesInput question={question} value={value} onChange={onChange} />;
  }
  // Normalize the BE's questionType so we can dispatch tolerantly:
  // BE has shipped at least four shapes for the multi-select question
  // ("medical_conditions" specifically): `multi_choice`, `multi-choice`,
  // `multiple_choice`, `checkbox`. Single-choice has shown up as
  // `single_choice`, `radio`, `dropdown`, `select`. Boolean as `yes_no`
  // or `boolean`. Normalize once instead of leaking the variants into
  // each `case`.
  const rawType = String(question.questionType || "").toLowerCase().trim();
  const optionCount = Array.isArray(question.options) ? question.options.length : 0;

  const normalizedType = (() => {
    // Pin against the BE's canonical discriminators FIRST. The
    // selectAllThatApply flag is later used as a fallback signal for
    // generic/unknown question types — but it must never override a
    // typed renderer like `medication_list` / `body_diagram` /
    // `photo_upload`, which all have `multiSelect=true` in the
    // contract but ship their own dedicated components.
    if (["medication_list", "medication-list", "drug_autocomplete", "drug-autocomplete", "medication", "medications", "drug_search", "drug-search", "med_search", "rxnorm", "rxnorm_search", "medication_search", "medications_search"].includes(rawType)) {
      if (rawType === "drug_autocomplete" || rawType === "drug-autocomplete") {
        // eslint-disable-next-line no-console
        console.warn(`[VOYA9] Encountered deprecated questionType="drug_autocomplete". BE contract pins this question to "medication_list".`);
      }
      return "drug_autocomplete";
    }
    if (["body_diagram", "body-diagram", "body_map", "body-map"].includes(rawType)) return "body_diagram";
    if (["photo_upload", "photo-upload", "photo", "image_upload", "file_upload"].includes(rawType)) return "photo_upload";
    if (["managed_multi_choice", "managed-multi-choice"].includes(rawType)) return "multi_choice";
    if (["multi_choice", "multi-choice", "multiple_choice", "multiple-choice", "checkbox", "checkboxes", "multiselect", "multi_select", "multi-select"].includes(rawType)) return "multi_choice";
    if (["single_choice", "single-choice", "radio", "select", "dropdown", "enum"].includes(rawType)) return "single_choice";
    if (["yes_no", "yes-no", "boolean", "bool"].includes(rawType)) return "yes_no";
    if (["long_text", "long-text", "textarea"].includes(rawType)) return "long_text";
    if (["number", "numeric", "int", "integer", "float", "decimal"].includes(rawType)) return "number";
    if (["scale", "rating", "slider"].includes(rawType)) return "scale";
    if (["date", "dob", "datepicker"].includes(rawType)) return "date";

    // ─── Fallbacks for generic/missing type strings ──────────────
    // Now (and ONLY now) the explicit BE multi-choice flag can win,
    // because we know the question didn't ship one of the canonical
    // typed renderers above.
    if (question.selectAllThatApply === true) return "multi_choice";

    // ─── Deprecated heuristics (kept temporarily) ─────────────────────
    // The BE now ships a stable contract — `medication_list` is the
    // canonical medications discriminator and `selectAllThatApply: true`
    // is the canonical multi-choice flag. The two heuristics below are
    // kept ONLY so in-progress sessions bound to drifted schemas keep
    // rendering. Each heuristic match logs a warning so we can audit
    // when it's safe to delete them entirely.
    {
      const keyLower = (question.questionKey || "").toLowerCase();
      const stemLower = `${question.questionText || ""} ${question.helpText || ""} ${question.description || ""}`.toLowerCase();
      if (
        keyLower === "medications" ||
        keyLower === "current_medications" ||
        keyLower === "medication_list" ||
        keyLower === "drugs" ||
        /medication/.test(keyLower) ||
        /drug.*search/.test(keyLower) ||
        /current medications/.test(stemLower)
      ) {
        if (rawType && rawType !== "medication_list") {
          // eslint-disable-next-line no-console
          console.warn(`[VOYA9] medications question shipped with non-canonical questionType="${rawType}". Expected "medication_list".`);
        }
        return "drug_autocomplete";
      }
    }
    // Stem-pattern multi-choice fallback (legacy schemas only).
    if (optionCount > 1) {
      const stem = `${question.questionText || ""} ${question.helpText || ""} ${question.description || ""}`.toLowerCase();
      const looksMulti = question.selectAllThatApply === true
        || /which of these/.test(stem)
        || /select all that apply/.test(stem)
        || /check all that apply/.test(stem)
        || /apply to you/.test(stem)
        || (question.questionKey || "").toLowerCase().includes("conditions")
        || (question.questionKey || "").toLowerCase().includes("allergies");
      if (looksMulti && question.selectAllThatApply !== true) {
        // eslint-disable-next-line no-console
        console.warn(`[VOYA9] multi-choice inferred from stem on question="${question.questionKey || "?"}" type="${rawType || "?"}". BE should ship selectAllThatApply: true.`);
      }
      return looksMulti ? "multi_choice" : "single_choice";
    }
    return rawType || "text";
  })();

  switch (normalizedType) {
    case "long_text": return <TextareaInput question={question} value={value} onChange={onChange} />;
    case "single_choice": return <RadioGroup question={question} value={value} onChange={onChange} />;
    case "multi_choice": return <CheckboxGrid question={question} value={value || []} onChange={onChange} />;
    case "scale": return <ScaleSlider question={question} value={value} onChange={onChange} />;
    case "date": return <DateInput question={question} value={value} onChange={onChange} />;
    case "photo_upload": return <PhotoUpload question={question} value={value} onChange={onChange} />;
    case "drug_autocomplete": return <DrugAutocomplete question={question} value={value} onChange={onChange} />;
    case "yes_no": return <YesNoToggle question={question} value={value} onChange={onChange} />;
    case "number": return <NumberInput question={question} value={value} onChange={onChange} />;
    case "body_diagram": return <BodyDiagramStub question={question} value={value} onChange={onChange} />;
    case "text":
    default: return <TextInput question={question} value={value} onChange={onChange} />;
  }
}

/* ---------- Validation ---------- */
export function isAnswerValid(q, value) {
  if (!q) return false;
  const required = q.required !== false; // default required

  // Managed-multi-choice envelope shape: {selections, otherText,
  // noneSelected}. Required iff at least one of the three is populated.
  // Per-option `followupRequired` is enforced too: if an option with
  // `hasSubFollowup` is selected and the BE marked followupRequired,
  // its followupValue must be non-empty.
  //
  // IMPORTANT: skip this branch when the value is actually a
  // `medication_list` envelope `{medications, noneSelected}` — both
  // shapes share the `noneSelected` key, so we'd otherwise treat a
  // populated medications list as an empty managed-choice answer and
  // refuse to enable Continue. Detect by the unique `medications`
  // field and fall through to the medication branch below.
  if (
    value && typeof value === "object" && !Array.isArray(value)
    && !Array.isArray(value.medications)
    && (Array.isArray(value.selections) || "otherText" in value || ("noneSelected" in value && !("medications" in value)))
  ) {
    const env = value;
    const hasSomething = (Array.isArray(env.selections) && env.selections.length > 0)
      || env.noneSelected === true
      || (typeof env.otherText === "string" && env.otherText.trim().length > 0);
    if (!required) return true;
    if (!hasSomething) return false;
    // Walk option follow-up requirements.
    const optList = Array.isArray(q.options) ? q.options : [];
    for (const sel of env.selections) {
      const opt = optList.find((o) => (typeof o === "object" ? o.key === sel.key : o === sel.key));
      if (opt && typeof opt === "object" && opt.hasSubFollowup && opt.followupRequired) {
        if (!sel.followupValue || !String(sel.followupValue).trim()) return false;
      }
    }
    return true;
  }

  const empty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
  if (!required && empty) return true;
  // Drug autocomplete: members can legitimately submit an empty list
  // ("if you take none, tap the 'no medications' tile"). The presence
  // of the search affordance counts as the member having engaged with
  // the question, so we treat empty-array as valid even when the
  // question is "required". Envelope shape `{medications,
  // noneSelected}` is also valid in any of its populated states.
  const rawTypeForValidate = String(q.questionType || "").toLowerCase();
  if (["medication_list", "medication-list", "drug_autocomplete", "drug-autocomplete", "medication", "medications"].includes(rawTypeForValidate)) return true;
  if (["body_diagram", "body-diagram", "body_map", "body-map"].includes(rawTypeForValidate)) return true;
  if (empty) return false;
  // Accept any of the multi-choice variants the BE has shipped over
  // time so validation matches the renderer's normalized dispatch.
  const t = String(q.questionType || "").toLowerCase();
  const isMultiChoice = ["multi_choice", "multi-choice", "multiple_choice", "multiple-choice", "checkbox", "checkboxes", "multiselect", "multi_select", "multi-select", "managed_multi_choice", "managed-multi-choice"].includes(t)
    // Explicit BE flag (preferred — set on canonical schema). When
    // present, we trust it over the type string so a future
    // `single_choice` question with `selectAllThatApply: true` would
    // be treated as multi (vanishingly unlikely but cheap insurance).
    || q.selectAllThatApply === true;
  if (isMultiChoice) return Array.isArray(value) && value.length > 0;
  if (q.min != null && Number(value) < Number(q.min)) return false;
  if (q.max != null && Number(value) > Number(q.max)) return false;
  return true;
}

/* ---------- Conditional logic ----------
   Question visibility rules. The backend has shipped a couple of shapes
   over time, so the evaluator accepts all of them and falls open (visible)
   when nothing matches:

   Legacy / inline form:
   - question.showIf:   { questionKey, equals|notEquals|includes|anyOf, value? }
   - question.showIfAll: [ ... ]   // every condition must pass
   - question.showIfAny: [ ... ]   // at least one must pass

   Admin-UI envelope (matches the "ALL of these are true" picker):
   - question.conditionalLogic:
       { operator: "all" | "any",
         conditions: [
           { questionKey, operator: "equals" | "not_equals" | "includes" | "in",
             value: "..." }
         ] }
   - question.showIf may also arrive as that envelope.
*/

// Single condition checker. Accepts either the legacy `{equals: ...}` style
// or the admin envelope `{operator: "equals", value: ...}` style.
function checkCondition(cond, responses) {
  if (!cond) return true;
  let target = responses[cond.questionKey];

  // Managed-multi-choice envelope shape: when the target answer is a
  // dict `{selections, otherText, noneSelected}`, flatten it into the
  // selection-key array that BE conditional rules expect. `none` keyword
  // is also surfaced so admin rules like `includes "none"` keep working.
  if (target && typeof target === "object" && !Array.isArray(target) && Array.isArray(target.selections)) {
    const keys = target.selections.map((s) => s?.key).filter(Boolean);
    if (target.noneSelected) keys.push("none");
    if (target.otherText && String(target.otherText).trim()) keys.push("other");
    target = keys;
  }

  // Legacy shorthand fields take precedence when present.
  if (cond.equals !== undefined) return Array.isArray(target) ? target.includes(cond.equals) : target === cond.equals;
  if (cond.notEquals !== undefined) return Array.isArray(target) ? !target.includes(cond.notEquals) : target !== cond.notEquals;
  if (cond.includes !== undefined) return Array.isArray(target) && target.includes(cond.includes);
  if (Array.isArray(cond.anyOf)) return Array.isArray(target)
    ? cond.anyOf.some((v) => target.includes(v))
    : cond.anyOf.includes(target);

  // Admin envelope: { operator, value }
  const op = (cond.operator || "").toLowerCase();
  const v = cond.value;
  if (op === "equals" || op === "=" || op === "is" || op === "eq") return Array.isArray(target) ? target.includes(v) : target === v;
  if (op === "not_equals" || op === "neq" || op === "!=") return Array.isArray(target) ? !target.includes(v) : target !== v;
  if (op === "in" || op === "any_of") return Array.isArray(v)
    ? (Array.isArray(target) ? v.some((x) => target.includes(x)) : v.includes(target))
    : false;
  if (op === "includes" || op === "contains") return Array.isArray(target) && target.includes(v);
  if (op === "is_empty") return target === undefined || target === null || target === "" || (Array.isArray(target) && target.length === 0);
  if (op === "is_not_empty") return !(target === undefined || target === null || target === "" || (Array.isArray(target) && target.length === 0));

  // Unknown shape — fail open so we never accidentally hide a question
  // because of a backend revision the frontend doesn't recognise yet.
  return true;
}

// Normalise an envelope into { all: bool, conditions: [...] }.
function envelopeToList(env) {
  if (!env || !Array.isArray(env.conditions)) return null;
  const op = (env.operator || env.logic || "all").toLowerCase();
  return { all: op !== "any" && op !== "or", conditions: env.conditions };
}

// Heuristics for the defensive sex-specific gate used by
// `evaluateConditional`. Both helpers are intentionally permissive:
// false-negatives are fine (BE schema still wins), but a false-positive
// (showing breastfeeding to a male) is a clinical credibility issue.
//
// `isFemaleOnlyQuestion` matches a small whitelist of phrases on either
// the questionKey or questionText. Add to FEMALE_ONLY_PATTERNS if the
// admin authors new variants the heuristic doesn't catch.
const FEMALE_ONLY_PATTERNS = [
  /breastfeed/i,
  /pregnan/i,         // pregnant, pregnancy
  /trying to conceive/i,
  /menstrua/i,        // menstrual, menstruating
  /menopaus/i,        // menopause, menopausal
  /postpartum/i,
  /lactat/i,          // lactation, lactating
];
function isFemaleOnlyQuestion(q) {
  if (!q) return false;
  const blob = `${q.questionKey || ""} ${q.questionText || ""} ${q.label || ""}`;
  return FEMALE_ONLY_PATTERNS.some((re) => re.test(blob));
}

// Find the member's self-reported sex anywhere in the responses map.
// Returns lowercased "male" / "female" / "other" / null. We check a few
// known questionKey shapes because the BE has shipped at least two
// variants over time (`sex` and `biological_sex`).
const SEX_KEYS = ["sex", "biological_sex", "sex_at_birth", "gender_at_birth", "gender"];
function getReportedSex(responses) {
  if (!responses || typeof responses !== "object") return null;
  for (const k of SEX_KEYS) {
    const raw = responses[k];
    if (raw == null || raw === "") continue;
    const v = String(raw).trim().toLowerCase();
    if (v.startsWith("male") || v === "m") return "male";
    if (v.startsWith("female") || v === "f") return "female";
    return v;
  }
  return null;
}

export function evaluateConditional(q, responses) {
  if (!q) return true;

  // ---- Defensive sex-specific gate ----
  // Clinical safety net: pregnancy / breastfeeding / menstrual / postpartum
  // questions should never be asked of a member who has self-identified
  // as male, even if the BE schema is missing the `conditionalLogic`
  // rule. We look at any prior response whose questionKey hints at sex
  // (sex / biological_sex / gender_at_birth / sex_at_birth) and compare
  // its value loosely (string-insensitive). Falls open when sex isn't
  // captured yet, so onboarding flows that haven't asked sex yet still
  // show the question — and `equals: "Female"` BE rules continue to
  // work as the primary control once they ship.
  if (isFemaleOnlyQuestion(q) && getReportedSex(responses) === "male") {
    return false;
  }
  // ---- /defensive sex gate ----

  // 1) Explicit list-form fields.
  if (Array.isArray(q.showIfAll) && q.showIfAll.length > 0) {
    return q.showIfAll.every((c) => checkCondition(c, responses));
  }
  if (Array.isArray(q.showIfAny) && q.showIfAny.length > 0) {
    return q.showIfAny.some((c) => checkCondition(c, responses));
  }

  // 2) Envelope on `conditionalLogic`, `conditionalLogic.showIf`,
  //    `showIf`, or `showIf.showIf`. The admin UI wraps the rule
  //    inside conditionalLogic.showIf, so we drill in.
  const candidates = [
    q.conditionalLogic,
    q.conditionalLogic && q.conditionalLogic.showIf,
    q.showIf,
    q.showIf && typeof q.showIf === "object" ? q.showIf.showIf : null,
  ].filter(Boolean);

  for (const cand of candidates) {
    const env = envelopeToList(cand);
    if (env) {
      if (env.conditions.length === 0) continue;
      return env.all
        ? env.conditions.every((c) => checkCondition(c, responses))
        : env.conditions.some((c) => checkCondition(c, responses));
    }
    // Single inline condition (no `conditions` array but has a questionKey).
    if (cand && typeof cand === "object" && cand.questionKey) {
      return checkCondition(cand, responses);
    }
  }

  return true;
}

/* =============================================================
 *  Renderers
 * ============================================================= */
const inputStyle = {
  width: "100%", padding: "16px 18px",
  background: C.cream, color: C.ink,
  border: `1.5px solid rgba(250,246,240,0.2)`,
  fontSize: 16, outline: "none",
  fontFamily: "inherit", boxSizing: "border-box", minWidth: 0,
};

function TextInput({ question, value, onChange }) {
  return (
    <input
      data-testid={`q-${question.questionKey}`}
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || ""}
      style={inputStyle}
      autoFocus
    />
  );
}

function NumberInput({ question, value, onChange }) {
  return (
    <input
      data-testid={`q-${question.questionKey}`}
      type="number"
      inputMode="numeric"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      placeholder={question.placeholder || ""}
      min={question.min}
      max={question.max}
      style={inputStyle}
    />
  );
}

// Friendly two-field input for heights stored in inches. Members enter
// feet (3–8) and inches (0–11) separately and we consolidate to a single
// integer-inches value before persisting — exactly the shape the backend
// expects, but without making members do the math themselves.
function FeetInchesInput({ question, value, onChange }) {
  const totalInches = Number.isFinite(Number(value)) && value !== "" ? Number(value) : null;
  const initialFt = totalInches != null ? Math.floor(totalInches / 12) : "";
  const initialIn = totalInches != null ? totalInches % 12 : "";
  const [ft, setFt] = useState(initialFt);
  const [inch, setInch] = useState(initialIn);

  const commit = (nextFt, nextIn) => {
    const f = nextFt === "" ? null : Number(nextFt);
    const i = nextIn === "" ? null : Number(nextIn);
    if (f == null && i == null) {
      onChange("");
      return;
    }
    const safeFt = Number.isFinite(f) ? f : 0;
    const safeIn = Number.isFinite(i) ? i : 0;
    onChange(safeFt * 12 + safeIn);
  };

  const onFtChange = (e) => {
    const v = e.target.value;
    setFt(v);
    commit(v, inch);
  };
  const onInChange = (e) => {
    let v = e.target.value;
    if (v !== "" && Number(v) > 11) v = "11";
    setInch(v);
    commit(ft, v);
  };

  const fieldStyle = { ...inputStyle, textAlign: "center" };
  const labelStyle = { fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.55)" };

  return (
    <div data-testid={`q-${question.questionKey}`} style={{ display: "flex", gap: 12 }}>
      <label style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={labelStyle}>FEET</span>
        <input
          data-testid={`q-${question.questionKey}-feet`}
          type="number"
          inputMode="numeric"
          min={3}
          max={8}
          value={ft}
          onChange={onFtChange}
          placeholder="5"
          style={fieldStyle}
        />
      </label>
      <label style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={labelStyle}>INCHES</span>
        <input
          data-testid={`q-${question.questionKey}-inches`}
          type="number"
          inputMode="numeric"
          min={0}
          max={11}
          value={inch}
          onChange={onInChange}
          placeholder="9"
          style={fieldStyle}
        />
      </label>
    </div>
  );
}

function TextareaInput({ question, value, onChange }) {
  return (
    <textarea
      data-testid={`q-${question.questionKey}`}
      rows={question.rows || 4}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || ""}
      style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
    />
  );
}

function DateInput({ question, value, onChange }) {
  // We replace the native <input type="date"> for brand consistency —
  // Safari/macOS renders a tiny system popover that ignores our theme,
  // and for date-of-birth specifically the calendar UI is genuinely bad
  // UX (members would have to tap "back" 30+ times to reach the 1980s).
  // A 3-select Day / Month / Year layout is faster on every device and
  // fully styleable in our peach/cream/ink palette.
  //
  // Storage shape stays unchanged: emit YYYY-MM-DD strings on change so
  // the round-trip with the backend continues to work.

  // Parse the incoming value into Y/M/D parts. Tolerates either a full
  // ISO timestamp ("2000-01-15T00:00:00Z") or a date-only string.
  const initial = (() => {
    if (!value) return { y: "", m: "", d: "" };
    const s = typeof value === "string" ? value.slice(0, 10) : "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return { y: "", m: "", d: "" };
    return { y: m[1], m: m[2], d: m[3] };
  })();

  const [y, setY] = useState(initial.y);
  const [m, setM] = useState(initial.m);
  const [d, setD] = useState(initial.d);

  // Only commit a full date when all three parts are present AND the
  // combination is valid (e.g. rejects Feb 30 by checking the Date
  // round-trips). Partial selections clear the parent value so the
  // "required" gate continues to fire.
  const commit = (yy, mm, dd) => {
    if (!yy || !mm || !dd) { onChange(""); return; }
    const iso = `${yy}-${mm}-${dd}`;
    const dt = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) { onChange(""); return; }
    // Reject e.g. 2026-02-30 which `Date` silently rolls forward.
    if (dt.getUTCFullYear() !== Number(yy) || dt.getUTCMonth() + 1 !== Number(mm) || dt.getUTCDate() !== Number(dd)) {
      onChange(""); return;
    }
    onChange(iso);
  };

  const onPart = (which) => (e) => {
    const v = e.target.value;
    if (which === "y") { setY(v); commit(v, m, d); }
    if (which === "m") { setM(v); commit(y, v, d); }
    if (which === "d") { setD(v); commit(y, m, v); }
  };

  const thisYear = new Date().getUTCFullYear();
  // Date-of-birth lookback: 110 years back, no future years. Most
  // recent year first so adults find their year near the top.
  const years = [];
  for (let i = thisYear; i >= thisYear - 110; i--) years.push(i);
  const months = [
    ["01", "January"], ["02", "February"], ["03", "March"], ["04", "April"],
    ["05", "May"], ["06", "June"], ["07", "July"], ["08", "August"],
    ["09", "September"], ["10", "October"], ["11", "November"], ["12", "December"],
  ];
  // Build a context-aware day list — caps to whatever the chosen
  // month actually has (with leap-year awareness via Date).
  const daysInMonth = (() => {
    const Y = Number(y) || 2000;
    const M = Number(m) || 1;
    return new Date(Date.UTC(Y, M, 0)).getUTCDate();
  })();
  const days = [];
  for (let i = 1; i <= daysInMonth; i++) days.push(String(i).padStart(2, "0"));

  const selectStyle = {
    width: "100%",
    padding: "16px 18px",
    background: C.cream,
    color: C.ink,
    border: `1.5px solid rgba(250,246,240,0.2)`,
    fontSize: 16,
    fontWeight: 600,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='%231a1a1a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 16px center",
    backgroundSize: "10px",
    paddingRight: 38,
    cursor: "pointer",
  };
  const labelStyle = { fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.55)" };

  // Compute member's age from the committed parts. Returns null until
  // a valid date is present so the preview only appears once the picker
  // has a complete, valid selection. Mirrors the validation in `commit`
  // so we never display "You're 0 years old" mid-selection.
  const ageYears = (() => {
    if (!y || !m || !d) return null;
    const iso = `${y}-${m}-${d}`;
    const dob = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(dob.getTime())) return null;
    if (dob.getUTCFullYear() !== Number(y) || dob.getUTCMonth() + 1 !== Number(m) || dob.getUTCDate() !== Number(d)) return null;
    const today = new Date();
    let age = today.getUTCFullYear() - dob.getUTCFullYear();
    // Subtract a year if their birthday hasn't happened yet this year.
    const monthDiff = today.getUTCMonth() - dob.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < dob.getUTCDate())) age -= 1;
    return age;
  })();

  return (
    <div data-testid={`q-${question.questionKey}`}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 12 }} className="!grid-cols-3">
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={labelStyle}>DAY</span>
          <select
            data-testid={`q-${question.questionKey}-day`}
            value={d}
            onChange={onPart("d")}
            style={selectStyle}
          >
            <option value="" disabled>—</option>
            {days.map((dd) => <option key={dd} value={dd}>{Number(dd)}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={labelStyle}>MONTH</span>
          <select
            data-testid={`q-${question.questionKey}-month`}
            value={m}
            onChange={onPart("m")}
            style={selectStyle}
          >
            <option value="" disabled>—</option>
            {months.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={labelStyle}>YEAR</span>
          <select
            data-testid={`q-${question.questionKey}-year`}
            value={y}
            onChange={onPart("y")}
            style={selectStyle}
          >
            <option value="" disabled>—</option>
            {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
          </select>
        </label>
      </div>
      {/* Brand-styled age preview — italic-serif tagline pattern used
          across the rest of the app for confirmation/affirmation moments.
          Renders only when the picker has a valid full date; if the
          entered age is sub-18 or impossibly old we still display the
          preview (validation lives elsewhere) but in coral so the member
          sees the value before submission. */}
      {ageYears !== null && (
        <div
          data-testid={`q-${question.questionKey}-age-preview`}
          style={{
            marginTop: 14,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            fontSize: 17,
            color: ageYears < 18 || ageYears > 110 ? "#F0A39A" : "#F4C9B2",
            letterSpacing: "0.005em",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <span style={{ fontStyle: "normal", fontWeight: 800, fontSize: 11, letterSpacing: "0.2em", color: "rgba(250,246,240,0.55)" }}>
            ★
          </span>
          <span>
            You’re <strong style={{ fontStyle: "normal", fontWeight: 800, color: "rgba(250,246,240,0.95)", fontFamily: 'inherit' }}>{ageYears}</strong> years old.
          </span>
        </div>
      )}
    </div>
  );
}

function RadioGroup({ question, value, onChange }) {
  const options = question.options || [];
  return (
    <div data-testid={`q-${question.questionKey}`} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {options.map((opt) => {
        const ov = typeof opt === "string" ? opt : opt.value;
        const ol = typeof opt === "string" ? opt : (opt.label || opt.value);
        const on = value === ov;
        return (
          <button
            key={ov}
            type="button"
            data-testid={`q-${question.questionKey}-opt-${ov}`}
            onClick={() => onChange(ov)}
            style={{
              padding: "16px 18px", textAlign: "left",
              background: on ? C.peach : "transparent",
              color: on ? C.ink : C.cream,
              border: `1.5px solid ${on ? C.peach : "rgba(250,246,240,0.25)"}`,
              fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}
          >
            {ol}
          </button>
        );
      })}
    </div>
  );
}

function YesNoToggle({ question, value, onChange }) {
  return (
    <div data-testid={`q-${question.questionKey}`} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {["yes", "no"].map((opt) => {
        const on = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`q-${question.questionKey}-${opt}`}
            onClick={() => onChange(opt)}
            style={{
              padding: "18px", textTransform: "uppercase",
              background: on ? C.peach : "transparent",
              color: on ? C.ink : C.cream,
              border: `1.5px solid ${on ? C.peach : "rgba(250,246,240,0.25)"}`,
              fontSize: 13, fontWeight: 800, letterSpacing: "0.2em", cursor: "pointer",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ---- Managed-list value envelope helpers ----
// BE contract for managed_multi_choice (lib/conditional.py):
//   {
//     selections: [{key, label, followupValue?}],
//     otherText?: string,
//     noneSelected?: boolean
//   }
// We hydrate legacy shapes (plain array of keys, or array containing
// "key:text" entries written by earlier FE builds) into the envelope so
// in-progress sessions don't lose answers, then always emit the
// canonical envelope going forward.
function asEnvelope(v) {
  if (v && typeof v === "object" && !Array.isArray(v) && (Array.isArray(v.selections) || "otherText" in v || "noneSelected" in v)) {
    return {
      selections: Array.isArray(v.selections) ? v.selections : [],
      otherText: v.otherText || "",
      noneSelected: v.noneSelected === true,
    };
  }
  if (Array.isArray(v)) {
    const selections = [];
    let otherText = "";
    let noneSelected = false;
    for (const item of v) {
      if (typeof item !== "string") continue;
      if (item === "none_of_the_above") { noneSelected = true; continue; }
      const colonIdx = item.indexOf(":");
      if (colonIdx > -1) {
        const key = item.slice(0, colonIdx);
        const tail = item.slice(colonIdx + 1);
        if (key === "other") { otherText = tail; continue; }
        selections.push({ key, followupValue: tail });
      } else {
        if (item === "other") continue; // bare "other" without text -> drop
        selections.push({ key: item });
      }
    }
    return { selections, otherText, noneSelected };
  }
  return { selections: [], otherText: "", noneSelected: false };
}
const SEVERITY_OPTIONS = ["Mild", "Moderate", "Severe"];

function CheckboxGrid({ question, value, onChange }) {
  // Managed lists are admin-curated picker contents fetched from the BE
  // (`/api/consumer/managed-lists/{slug}`). The BE returns options + a
  // `settings` block that owns the canonical None/Other labels and
  // follow-up max-lengths — we read those directly so the FE doesn't
  // drift from the brand voice (e.g. for allergies the None label is
  // "No known allergies (NKA)", not "None of the above").
  //
  // Answer shape: dict envelope `{selections, otherText, noneSelected}`.
  // Per-option `hasSubFollowup` answers ride on `selections[i].followupValue`.
  // Legacy shapes (plain array of keys) hydrate gracefully via asEnvelope().
  //
  // Falls back to inline `question.options[]` and a string-array value
  // when no managedListSlug is set (legacy paths that haven't migrated).
  const inlineOptions = question.options || [];
  const slug = question.managedListSlug;
  const [fetched, setFetched] = useState(null);
  const [settings, setSettings] = useState(null);
  const [fetchErr, setFetchErr] = useState(null);
  // Hoisted-above-conditional hooks for the managed-list path so we
  // satisfy rules-of-hooks even though they're only consumed when
  // `slug` is truthy. Cost is one extra state slot per legacy
  // (non-managed) CheckboxGrid — negligible.
  const envForInit = asEnvelope(value);
  const [otherOpen, setOtherOpen] = useState(!!envForInit.otherText);
  useEffect(() => {
    if (!slug) return;
    if (envForInit.otherText) setOtherOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envForInit.otherText, slug]);
  // One-shot hydration: if the parent passed a legacy array and we're
  // in managed-list mode, promote to the envelope shape so the rest of
  // the flow round-trips canonically.
  useEffect(() => {
    if (!slug) return;
    if (Array.isArray(value)) onChange(envForInit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      let res = null;
      try { res = await apiGet(`/consumer/managed-lists/${encodeURIComponent(slug)}`); }
      catch {
        try { res = await apiGet(`/managed-lists/${encodeURIComponent(slug)}`); }
        catch { res = null; }
      }
      if (cancelled) return;
      if (!res) { setFetchErr("unavailable"); setFetched([]); return; }
      const raw = Array.isArray(res) ? res
        : Array.isArray(res?.options) ? res.options
        : Array.isArray(res?.items) ? res.items
        : Array.isArray(res?.values) ? res.values
        : [];
      const items = raw.filter((o) => (typeof o === "string") || o?.active !== false);
      setFetched(items);
      setSettings(res?.settings || null);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const loading = slug && fetched === null && !fetchErr;

  // ----- managed-list mode (envelope value shape) -----
  if (slug) {
    const env = asEnvelope(value);

    const showNone = settings?.showNoneOption !== false;
    const noneLabel = settings?.noneLabel || "None of the above";
    const noneIsExclusive = settings?.noneIsExclusive !== false;
    const allowOther = settings?.allowOther !== false;
    const otherLabel = settings?.otherLabel || "Other (please describe)";
    const otherPrompt = settings?.otherFollowupPrompt || "Please describe…";
    const otherMaxLength = settings?.otherFollowupMaxLength || 250;

    // "Other" tile opens a textarea immediately on click. Visibility
    // state is hoisted above the conditional (rules-of-hooks).

    // Alphabetize BE items by label; None and Other are appended via
    // dedicated rows below (so their behaviour stays uniform regardless
    // of how the list is authored).
    const sortedItems = (fetched || []).slice().sort((a, b) => {
      const al = (typeof a === "string" ? a : (a.label ?? a.name ?? a.key ?? "")).toLowerCase();
      const bl = (typeof b === "string" ? b : (b.label ?? b.name ?? b.key ?? "")).toLowerCase();
      return al.localeCompare(bl);
    });

    const selectedKey = (key) => env.selections.find((s) => s.key === key);
    const isItemSelected = (key) => !!selectedKey(key);

    const commit = (next) => onChange(next);

    const toggleItem = (item) => {
      const key = item.key;
      const label = item.label;
      if (env.noneSelected && noneIsExclusive) {
        commit({ ...env, noneSelected: false, selections: [{ key, label }] });
        return;
      }
      if (isItemSelected(key)) {
        commit({ ...env, selections: env.selections.filter((s) => s.key !== key) });
      } else {
        commit({ ...env, selections: [...env.selections, { key, label }] });
      }
    };

    const toggleNone = () => {
      if (env.noneSelected) {
        commit({ ...env, noneSelected: false });
      } else {
        setOtherOpen(false);
        commit(noneIsExclusive
          ? { selections: [], otherText: "", noneSelected: true }
          : { ...env, noneSelected: true });
      }
    };

    const toggleOther = () => {
      // Tapping "Other" opens the textarea if closed; if it's open and
      // empty, closes it. If it's open with text, clears the text and
      // closes (one explicit deselect action).
      if (!otherOpen) {
        setOtherOpen(true);
        // Selecting Other also clears noneSelected when None is exclusive.
        if (env.noneSelected && noneIsExclusive) commit({ ...env, noneSelected: false });
        return;
      }
      setOtherOpen(false);
      commit({ ...env, otherText: "" });
    };

    const setFollowupValue = (key, val) => {
      const next = env.selections.map((s) => s.key === key ? { ...s, followupValue: val } : s);
      commit({ ...env, selections: next });
    };

    const setOtherText = (text) => {
      const clipped = text.slice(0, otherMaxLength);
      commit({ ...env, otherText: clipped });
    };

    return (
      <div data-testid={`q-${question.questionKey}`}>
        {loading && (
          <div style={{ padding: "20px 4px", fontSize: 13, color: "rgba(250,246,240,0.55)", fontFamily: '"Instrument Serif", serif', fontStyle: "italic" }}>
            Loading options…
          </div>
        )}
        {!loading && fetchErr && sortedItems.length === 0 && (
          <div style={{ padding: "20px 4px", fontSize: 13, color: "#F0A39A", fontFamily: '"Instrument Serif", serif', fontStyle: "italic" }}>
            We couldn’t load the picker list. Please refresh — if it keeps happening, message your care team.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="!grid-cols-1 sm:!grid-cols-2">
          {sortedItems.map((opt) => {
            const key = typeof opt === "string" ? opt : opt.key;
            const label = typeof opt === "string" ? opt : opt.label;
            const on = isItemSelected(key);
            return (
              <button
                key={key}
                type="button"
                data-testid={`q-${question.questionKey}-opt-${key}`}
                onClick={() => toggleItem(typeof opt === "string" ? { key, label } : opt)}
                style={{
                  padding: "14px 16px", textAlign: "left",
                  background: on ? C.peach : "transparent",
                  color: on ? C.ink : C.cream,
                  border: `1.5px solid ${on ? C.peach : "rgba(250,246,240,0.25)"}`,
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                {on ? "✓ " : ""}{label}
              </button>
            );
          })}
          {/* Synthetic Other tile — sourced from BE settings.otherLabel */}
          {allowOther && (
            <button
              type="button"
              data-testid={`q-${question.questionKey}-opt-other`}
              onClick={toggleOther}
              style={{
                padding: "14px 16px", textAlign: "left",
                background: otherOpen ? C.peach : "transparent",
                color: otherOpen ? C.ink : C.cream,
                border: `1.5px solid ${otherOpen ? C.peach : "rgba(250,246,240,0.25)"}`,
                fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              {otherOpen ? "✓ " : ""}{otherLabel}
            </button>
          )}
          {/* Synthetic None tile — sourced from BE settings.noneLabel.
              For allergies this becomes "No known allergies (NKA)". */}
          {showNone && (
            <button
              type="button"
              data-testid={`q-${question.questionKey}-opt-none`}
              onClick={toggleNone}
              style={{
                padding: "14px 16px", textAlign: "left",
                background: env.noneSelected ? C.peach : "transparent",
                color: env.noneSelected ? C.ink : C.cream,
                border: `1.5px solid ${env.noneSelected ? C.peach : "rgba(250,246,240,0.25)"}`,
                fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}
            >
              {env.noneSelected ? "✓ " : ""}{noneLabel}
            </button>
          )}
        </div>

        {/* Per-option follow-ups (severity pills for followupType === "severity",
            plain textarea for anything else). Renders below the grid for each
            currently-selected option with hasSubFollowup. */}
        {sortedItems
          .filter((opt) => typeof opt !== "string" && opt.hasSubFollowup && isItemSelected(opt.key))
          .map((opt) => {
            const cur = selectedKey(opt.key)?.followupValue || "";
            const prompt = (opt.followupPrompt || "Please describe").toUpperCase();
            const isSeverity = opt.followupType === "severity";
            return (
              <div key={`fu-${opt.key}`} style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.55)", marginBottom: 8 }}>
                  {opt.label.toUpperCase()} · {prompt}
                </div>
                {isSeverity ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {SEVERITY_OPTIONS.map((s) => {
                      const on = cur.toLowerCase() === s.toLowerCase();
                      return (
                        <button
                          key={s}
                          type="button"
                          data-testid={`q-${question.questionKey}-followup-${opt.key}-${s.toLowerCase()}`}
                          onClick={() => setFollowupValue(opt.key, s.toLowerCase())}
                          style={{
                            padding: "10px 22px", fontSize: 12, fontWeight: 800,
                            letterSpacing: "0.18em",
                            background: on ? C.peach : "transparent",
                            color: on ? C.ink : C.cream,
                            border: `1.5px solid ${on ? C.peach : "rgba(250,246,240,0.25)"}`,
                            borderRadius: 9999, cursor: "pointer", textTransform: "uppercase",
                          }}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <textarea
                    data-testid={`q-${question.questionKey}-followup-${opt.key}`}
                    value={cur}
                    onChange={(e) => setFollowupValue(opt.key, e.target.value)}
                    rows={2}
                    style={{
                      width: "100%", padding: "14px 16px",
                      background: C.cream, color: C.ink,
                      border: `1.5px solid rgba(250,246,240,0.2)`,
                      fontSize: 15, outline: "none", fontFamily: "inherit",
                      boxSizing: "border-box", resize: "vertical",
                    }}
                  />
                )}
              </div>
            );
          })}

        {/* "Other" free-text — shown when the Other tile is active. The
            textarea is bound to env.otherText and clipped to the BE-supplied
            max-length so we never silently overflow the BE limit. */}
        {allowOther && otherOpen && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.55)", marginBottom: 8 }}>
              {otherPrompt.toUpperCase()}
            </div>
            <textarea
              data-testid={`q-${question.questionKey}-other-text`}
              value={env.otherText || ""}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder={otherPrompt}
              rows={2}
              maxLength={otherMaxLength}
              autoFocus
              style={{
                width: "100%", padding: "14px 16px",
                background: C.cream, color: C.ink,
                border: `1.5px solid rgba(250,246,240,0.2)`,
                fontSize: 15, outline: "none", fontFamily: "inherit",
                boxSizing: "border-box", resize: "vertical",
              }}
            />
            <div style={{ marginTop: 6, fontSize: 11, color: "rgba(250,246,240,0.45)", letterSpacing: "0.05em" }}>
              {(env.otherText || "").length} / {otherMaxLength}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ----- inline-options legacy path (no managed-list slug) -----
  const toggle = (ov) => {
    const set = new Set(Array.isArray(value) ? value : []);
    set.has(ov) ? set.delete(ov) : set.add(ov);
    onChange(Array.from(set));
  };
  return (
    <div data-testid={`q-${question.questionKey}`} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="!grid-cols-1 sm:!grid-cols-2">
      {inlineOptions.map((opt) => {
        const ov = typeof opt === "string" ? opt : (opt.value ?? opt.key ?? opt.id ?? opt.label);
        const ol = typeof opt === "string" ? opt : (opt.label ?? opt.value);
        const on = (value || []).includes(ov);
        return (
          <button
            key={ov}
            type="button"
            data-testid={`q-${question.questionKey}-opt-${ov}`}
            onClick={() => toggle(ov)}
            style={{
              padding: "14px 16px", textAlign: "left",
              background: on ? C.peach : "transparent",
              color: on ? C.ink : C.cream,
              border: `1.5px solid ${on ? C.peach : "rgba(250,246,240,0.25)"}`,
              fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            {on ? "✓ " : ""}{ol}
          </button>
        );
      })}
    </div>
  );
}

function ScaleSlider({ question, value, onChange }) {
  const min = question.min ?? 1;
  const max = question.max ?? 10;
  const v = value ?? Math.round((min + max) / 2);
  useEffect(() => { if (value == null) onChange(v); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid={`q-${question.questionKey}`}>
      <div style={{ fontSize: 56, fontWeight: 800, letterSpacing: "-0.04em", color: C.peach, textAlign: "center" }}>{v}</div>
      <input
        type="range"
        min={min}
        max={max}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", marginTop: 14, accentColor: C.peach }}
      />
      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(250,246,240,0.6)", letterSpacing: "0.1em" }}>
        <span>{question.minLabel || min}</span>
        <span>{question.maxLabel || max}</span>
      </div>
    </div>
  );
}

function PhotoUpload({ question, value, onChange }) {
  // Frontend-only stub — renders a file input that base64 previews the
  // selected photo locally. Actual S3 upload wiring happens server-side
  // when the assessment submits and references the local data URL.
  const [preview, setPreview] = useState(value || null);
  const ref = useRef(null);
  const pick = () => ref.current?.click();
  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      setPreview(r.result);
      onChange({ name: f.name, size: f.size, type: f.type, dataUrl: r.result });
    };
    r.readAsDataURL(f);
  };
  return (
    <div data-testid={`q-${question.questionKey}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input type="file" accept="image/*" ref={ref} onChange={handle} style={{ display: "none" }} />
      {preview ? (
        <div style={{ background: "rgba(250,246,240,0.06)", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <img src={typeof preview === "string" ? preview : preview.dataUrl} alt="Upload preview" style={{ maxWidth: "100%", maxHeight: 360, objectFit: "contain" }} />
          <button type="button" data-testid={`q-${question.questionKey}-replace`} onClick={pick} style={photoBtnStyle}>REPLACE PHOTO</button>
        </div>
      ) : (
        <button type="button" data-testid={`q-${question.questionKey}-upload`} onClick={pick} style={{ ...photoBtnStyle, padding: "24px 18px" }}>
          TAP TO ADD PHOTO
        </button>
      )}
    </div>
  );
}
const photoBtnStyle = {
  padding: "14px 18px", background: "transparent", color: C.peach,
  border: `1.5px dashed rgba(216,139,92,0.5)`, fontSize: 12, fontWeight: 800,
  letterSpacing: "0.2em", cursor: "pointer",
};

function DrugAutocomplete({ question, value, onChange }) {
  // BE `questionType: "medication_list"` contract:
  //
  // Search API:
  //   GET /api/consumer/medications/search?q=<term>
  //   → [{rxnormCui, displayName, genericName, strength, doseForm}, ...]
  //
  // Answer envelope (one source of truth — matches BE validator
  // lib/conditional.py:_normalize_for_match and the safety-rule engine):
  //   {
  //     medications: [
  //       {rxnormCui, displayName, genericName?, strength?, doseForm?},
  //       {customText, displayName}                           // freeText
  //     ],
  //     noneSelected: false
  //   }
  //
  // Per-question BE settings consumed here:
  //   allowFreeText   → enables the "Can't find it?" CTA
  //   freeTextLabel   → CTA copy for the free-text fallback
  //   showNoneOption  → renders the mutex "none" tile
  //   noneLabel       → e.g. "I don't take any medications" (brand-specific)
  //
  // Legacy shape hydration: earlier FE builds stored a single record
  // (`{name, rxcui}`) or an array of records — both lift cleanly into
  // the envelope on first paint so in-progress sessions don't lose data.
  const settings = question || {};
  const allowFreeText = settings.allowFreeText !== false;
  const freeTextLabel = settings.freeTextLabel || "Can't find it? Add manually";
  const showNone = settings.showNoneOption !== false;
  const noneLabel = settings.noneLabel || "I don't take any medications";

  const initialEnv = (() => {
    if (value && typeof value === "object" && !Array.isArray(value) && (Array.isArray(value.medications) || "noneSelected" in value)) {
      return {
        medications: Array.isArray(value.medications) ? value.medications : [],
        noneSelected: value.noneSelected === true,
      };
    }
    if (Array.isArray(value)) {
      // Earlier FE builds stored an array of records — fold into envelope.
      const meds = value.map((v) => v.freeText
        ? { customText: v.displayName || v.name, displayName: v.displayName || v.name }
        : { rxnormCui: v.rxnormCui || v.rxcui, displayName: v.displayName || v.name, genericName: v.genericName, strength: v.strength, doseForm: v.doseForm }).filter((m) => m.rxnormCui || m.customText);
      return { medications: meds, noneSelected: false };
    }
    if (value && typeof value === "object") {
      // Single-record legacy shape.
      const single = value.freeText
        ? { customText: value.displayName || value.name, displayName: value.displayName || value.name }
        : { rxnormCui: value.rxnormCui || value.rxcui, displayName: value.displayName || value.name };
      return single.rxnormCui || single.customText ? { medications: [single], noneSelected: false } : { medications: [], noneSelected: false };
    }
    return { medications: [], noneSelected: false };
  })();

  const [env, setEnv] = useState(initialEnv);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  // Manual-entry composer (matches admin preview UX): a permanent
  // "+ Can't find it? Add manually" link below the search reveals
  // a one-row form with a text field + Add/Cancel buttons so members
  // can attach off-catalog medications (compounds, regional brands,
  // research peptides). Visible only when `allowFreeText` is true.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");

  // One-shot hydration upward when value arrived in legacy shape.
  useEffect(() => {
    const isEnvelope = value && typeof value === "object" && !Array.isArray(value) && ("medications" in value || "noneSelected" in value);
    if (!isEnvelope) onChange(initialEnv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      // Canonical endpoint per BE contract; legacy `/drug-search`
      // kept as a fallback for any preview environments that haven't
      // shipped the rename yet.
      let r = null;
      try { r = await apiGet(`/consumer/medications/search?q=${encodeURIComponent(query)}`); }
      catch { try { r = await apiGet(`/consumer/drug-search?q=${encodeURIComponent(query)}`); } catch { r = null; } }
      if (cancelled) return;
      setResults(Array.isArray(r) ? r : (r?.results || []));
      setLoading(false);
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const commit = (next) => { setEnv(next); onChange(next); };

  const add = (med) => {
    // Selecting any med turns off "none".
    const dedupKey = med.rxnormCui || med.customText || med.displayName;
    if (env.medications.some((m) => (m.rxnormCui || m.customText || m.displayName) === dedupKey)) {
      setQuery(""); setResults([]); return;
    }
    commit({ medications: [...env.medications, med], noneSelected: false });
    setQuery(""); setResults([]);
  };

  const remove = (idx) => commit({ ...env, medications: env.medications.filter((_, i) => i !== idx) });

  const toggleNone = () => {
    if (env.noneSelected) commit({ ...env, noneSelected: false });
    else commit({ medications: [], noneSelected: true });
  };

  const addFreeText = () => {
    if (!query || !query.trim()) return;
    const text = query.trim();
    add({ customText: text, displayName: text });
  };

  const showDropdown = focused && query.length > 0 && !env.noneSelected;

  return (
    <div data-testid={`q-${question.questionKey}`}>
      {/* Selected medications stack */}
      {env.medications.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {env.medications.map((m, i) => (
            <div
              key={(m.rxnormCui || m.customText || m.displayName) + i}
              data-testid={`q-${question.questionKey}-item-${i}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                background: C.peach,
                color: C.ink,
                padding: "12px 14px",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, color: C.ink, wordBreak: "break-word" }}>
                  {m.displayName || m.genericName || m.customText || "Custom entry"}
                </div>
                {m.customText && (
                  <div style={{ marginTop: 2, fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", color: "rgba(0,0,0,0.55)" }}>
                    ADDED MANUALLY · UNVERIFIED
                  </div>
                )}
                {!m.customText && (m.strength || m.doseForm) && (
                  <div style={{ marginTop: 2, fontSize: 12, color: "rgba(0,0,0,0.65)" }}>
                    {[m.strength, m.doseForm].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`q-${question.questionKey}-remove-${i}`}
                aria-label="Remove medication"
                style={{ background: "transparent", border: 0, color: C.ink, fontSize: 18, lineHeight: 1, padding: "0 6px", cursor: "pointer", fontWeight: 700 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search input + dropdown — disabled when "none" is selected */}
      {!env.noneSelected && (
        <div style={{ position: "relative" }}>
          <input
            data-testid={`q-${question.questionKey}-search`}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder={question.placeholder || (env.medications.length === 0 ? "Start typing a medication…" : "Add another medication…")}
            style={inputStyle}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {showDropdown && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: C.ink, border: `1px solid rgba(250,246,240,0.15)`, maxHeight: 320, overflowY: "auto", zIndex: 5 }}>
              {loading && <div style={{ padding: 14, fontSize: 13, color: "rgba(250,246,240,0.6)" }}>Searching…</div>}
              {(() => {
                // Filter to clinically actionable hits — i.e. entries
                // that carry a strength (e.g. "metoprolol tartrate 50
                // MG Oral Tablet"). RxNorm's ingredient-only concepts
                // (just "metoprolol") aren't useful for the physician
                // because they don't carry a dose, and they clutter
                // the dropdown. Members can still add an unusual
                // entry via the "Can't find it? Add manually" link
                // below.
                const filtered = results.filter((r) => {
                  const s = r.strength;
                  if (s == null) return false;
                  if (typeof s === "string") return s.trim().length > 0;
                  return true;
                });
                if (!loading && filtered.length === 0 && results.length > 0 && query.length >= 2) {
                  return (
                    <div data-testid="medication-search-no-dosed" style={{ padding: 14, fontSize: 13, color: "rgba(250,246,240,0.55)" }}>
                      No dosed match for “{query}”. Try a different spelling — or use “Add manually” below to enter the brand and strength.
                    </div>
                  );
                }
                if (!loading && filtered.length === 0 && results.length === 0 && query.length >= 2) {
                  return (
                    <div style={{ padding: 14, fontSize: 13, color: "rgba(250,246,240,0.55)" }}>
                      No matches for “{query}”. Try a different spelling — or use “Add manually” below.
                    </div>
                  );
                }
                return filtered.slice(0, 8).map((r) => (
                  <button
                    key={r.rxnormCui || r.displayName}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => add({ rxnormCui: r.rxnormCui, displayName: r.displayName, genericName: r.genericName, strength: r.strength, doseForm: r.doseForm })}
                    style={{ width: "100%", padding: "12px 14px", textAlign: "left", background: "transparent", color: C.cream, border: 0, borderBottom: `1px solid rgba(250,246,240,0.08)`, fontSize: 14, cursor: "pointer" }}
                  >
                    <div style={{ lineHeight: 1.35 }}>{r.displayName || r.genericName}</div>
                    {(r.strength || r.doseForm) && (
                      <div style={{ marginTop: 2, fontSize: 11, color: "rgba(250,246,240,0.55)", letterSpacing: "0.02em" }}>
                        {[r.strength, r.doseForm].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </button>
                ));
              })()}
              {!loading && results.length === 0 && query.length === 1 && (
                <div style={{ padding: 14, fontSize: 13, color: "rgba(250,246,240,0.55)" }}>Keep typing to search…</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Permanent "+ Can't find it? Add manually" affordance + composer.
          Mirrors the admin preview's manual-entry UX so members
          discovering off-catalog medications never feel stuck. Visible
          only when the BE settings allow free text. */}
      {!env.noneSelected && allowFreeText && !manualOpen && (
        <button
          type="button"
          data-testid={`q-${question.questionKey}-manual-open`}
          onClick={() => { setManualOpen(true); setQuery(""); setResults([]); }}
          style={{
            marginTop: 10,
            background: "transparent",
            border: 0,
            color: C.peach,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            cursor: "pointer",
            padding: "6px 0",
            textAlign: "left",
          }}
        >
          + {freeTextLabel}
        </button>
      )}
      {!env.noneSelected && allowFreeText && manualOpen && (
        <div
          data-testid={`q-${question.questionKey}-manual-composer`}
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            alignItems: "stretch",
          }}
        >
          <input
            type="text"
            data-testid={`q-${question.questionKey}-manual-input`}
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && manualText.trim()) {
                e.preventDefault();
                add({ customText: manualText.trim(), displayName: manualText.trim() });
                setManualText("");
                setManualOpen(false);
              } else if (e.key === "Escape") {
                setManualOpen(false);
                setManualText("");
              }
            }}
            placeholder="Type the medication name"
            autoFocus
            style={{ ...inputStyle, flex: 1, marginTop: 0 }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            data-testid={`q-${question.questionKey}-manual-add`}
            disabled={!manualText.trim()}
            onClick={() => {
              if (!manualText.trim()) return;
              add({ customText: manualText.trim(), displayName: manualText.trim() });
              setManualText("");
              setManualOpen(false);
            }}
            style={{
              padding: "0 22px",
              background: manualText.trim() ? C.peach : "rgba(244,201,178,0.35)",
              color: C.ink,
              border: 0,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.18em",
              cursor: manualText.trim() ? "pointer" : "not-allowed",
              textTransform: "uppercase",
            }}
          >
            Add
          </button>
          <button
            type="button"
            data-testid={`q-${question.questionKey}-manual-cancel`}
            onClick={() => { setManualOpen(false); setManualText(""); }}
            style={{
              padding: "0 14px",
              background: "transparent",
              color: "rgba(250,246,240,0.55)",
              border: 0,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.18em",
              cursor: "pointer",
              textTransform: "uppercase",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Mutex "none" tile — brand-specific copy from BE settings */}
      {showNone && (
        <button
          type="button"
          data-testid={`q-${question.questionKey}-none`}
          onClick={toggleNone}
          style={{
            marginTop: 14,
            padding: "14px 16px",
            textAlign: "left",
            background: env.noneSelected ? C.peach : "transparent",
            color: env.noneSelected ? C.ink : C.cream,
            border: `1.5px solid ${env.noneSelected ? C.peach : "rgba(250,246,240,0.25)"}`,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
          }}
        >
          {env.noneSelected ? "✓ " : ""}{noneLabel}
        </button>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: "rgba(250,246,240,0.55)", fontFamily: '"Instrument Serif", serif', fontStyle: "italic" }}>
        {env.noneSelected
          ? "You can continue."
          : env.medications.length === 0
            ? "If you take none, tap the tile below to confirm."
            : `${env.medications.length} medication${env.medications.length === 1 ? "" : "s"} added.`}
      </div>
    </div>
  );
}


// Placeholder for `questionType: "body_diagram"` (multi-select map of
// body regions where the member is experiencing concerns). The BE
// contract reserves this slot but the rendering work is still ahead —
// rather than crash or fall through to a confusing text input, we
// render a brand-styled "this surface isn't ready yet" notice so the
// flow can continue. Members who hit a body_diagram question can press
// "Continue" past it (validator below returns true for any value).
//
// Value contract (from BE descriptor): `{regions: [{regionKey,
// severity?, notes?}], noneSelected: bool}`. We commit `{regions: [],
// noneSelected: true}` so the answer satisfies the BE validator if a
// member's session genuinely includes one of these questions.
function BodyDiagramStub({ question, value, onChange }) {
  useEffect(() => {
    if (!value || (typeof value === "object" && !Array.isArray(value.regions))) {
      onChange({ regions: [], noneSelected: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid={`q-${question.questionKey}`} style={{
      padding: "18px 18px",
      border: `1px dashed rgba(250,246,240,0.3)`,
      background: "rgba(250,246,240,0.02)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.55)" }}>
        ★ COMING SOON
      </div>
      <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: C.cream }}>
        Body-region picker is coming. For now, press <strong>Continue</strong> to skip — your physician will follow up on specifics during your consultation.
      </p>
    </div>
  );
}
